import {
  PasswordGuessabilityHint,
  validatePasswordStrength,
} from 'common/utils/validatePasswordStrength';
import { IntlShape } from 'react-intl';

const messageByReason: Record<
  'too_short' | 'missing_complexity',
  { id: string; defaultMessage: string }
> = {
  too_short: {
    id: 'is.input.new_password_too_short',
    defaultMessage: 'The password must be at least 12 characters long.',
  },
  missing_complexity: {
    id: 'is.input.new_password_missing_complexity',
    defaultMessage:
      'The password must include an uppercase letter, a lowercase letter, a number and a special character.',
  },
};

const genericTooGuessableMessage = {
  id: 'is.input.new_password_too_guessable',
  defaultMessage:
    'The password is too common or easy to guess. Please choose a stronger password.',
};

// More specific tips for the too_guessable case, based on zxcvbn's feedback.
const messageByGuessabilityHint: Record<
  PasswordGuessabilityHint,
  { id: string; defaultMessage: string }
> = {
  common_word: {
    id: 'is.input.new_password_too_guessable_common_word',
    defaultMessage:
      'The password is too common or easy to guess. Try combining a few unrelated words instead of a single common word or name.',
  },
  pattern: {
    id: 'is.input.new_password_too_guessable_pattern',
    defaultMessage:
      'The password is too common or easy to guess. Avoid predictable patterns like repeated characters, keyboard sequences, or letter substitutions.',
  },
  dates: {
    id: 'is.input.new_password_too_guessable_dates',
    defaultMessage:
      'The password is too common or easy to guess. Avoid using dates or years.',
  },
  pwned: {
    id: 'is.input.new_password_too_guessable_pwned',
    defaultMessage:
      'This password has appeared in known data breaches. Please choose a different one.',
  },
};

export function validateNewPasswordStrength(intl: IntlShape) {
  return {
    validate: (value: string) => {
      const result = validatePasswordStrength(value);
      if (result.valid) {
        return true;
      }
      if (result.reason === 'too_guessable') {
        const message = result.hint
          ? messageByGuessabilityHint[result.hint]
          : genericTooGuessableMessage;
        return intl.formatMessage(message);
      }
      return intl.formatMessage(messageByReason[result.reason]);
    },
  };
}
